import { Injectable, inject } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, filter, first, map, share, switchMap, tap } from 'rxjs/operators';
import { BonusStatus, BonusStatusLabel, BonusType } from './data/user-bonuses.data';
import { CommonDataService } from '../common-data.service';
import { GamesService } from '../games/games.service';
import { FiltersService } from '../filters.service';
import { PlatformGraphQlService } from '../graphql/platform-graphql.service';
import { OffersService } from '../offers.service';
import { UserService } from './user.service';
import { UserGroupsService } from './user-groups.service';
import { BONUS_PACKS } from '../../../page/bonuses/data/bonus.data';

export enum BonusCodeStatus {
  FAILED_TO_ACTIVATE = 'failed_to_activate',
  ALREADY_ACTIVATED = 'already_activated',
  SUCCESSFULLY_ACTIVATED = 'successfully_activated',
}

@Injectable({
  providedIn: 'root',
})
export class UserBonusesService {
  private _platformApi = inject(PlatformGraphQlService);
  private _data = inject(CommonDataService);
  private _games = inject(GamesService);
  private _filters = inject(FiltersService);
  private _offers = inject(OffersService);
  private _user = inject(UserService);
  private _userGroup = inject(UserGroupsService);

  /**
   * All spins bonuses
   */
  public bonuses$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  public freespinBonusList$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  public cashBonusList$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  public amountBonusChange$: ReplaySubject<any> = new ReplaySubject<any>(1);
  public isAllBonusUsed: boolean = null;
  public bonusList$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

  private _isBonusListEmpty: boolean = null;

  private _bonusStatusListForShow = [
    BonusStatus.ACTIVE,
    BonusStatus.IN_PROGRESS,
    BonusStatus.FREESPIN_IN_PROGRESS,
    BonusStatus.FREESPIN_RELEASED,
    BonusStatus.NEW,
    BonusStatus.ACTIVATION_EXPIRED,
    BonusStatus.USAGE_EXPIRED,
    BonusStatus.WAGERING_USAGE_EXPIRED,
    BonusStatus.RELEASED,
  ];

  private _hasActiveFsBonus: boolean = null;

  private _hasActiveCashBonus: boolean = null;

  public get isCurrentListEmpty(): boolean {
    return this._isBonusListEmpty;
  }

  public get Stage() {
    return BonusStatus;
  }

  public get Type() {
    return BonusType;
  }

  public get hasActiveFsBonus(): boolean {
    return this._hasActiveFsBonus;
  }

  public get hasActiveCashBonus(): boolean {
    return this._hasActiveCashBonus;
  }

  /**
   * Load list of player money bonuses
   */
  public loadBonusList() {
    this._platformApi.bonusList(true).pipe(
      first(),
      map((res: any) => this._resolveBonusList(res?.data?.result?.info?.player?.walletsData?.walletList || [])),
      switchMap((list: any[]) => this._mapFreespinBonusGames(list || [])),
      map((list: any[]) => this._mapBonuses(list || [])),
      map((list: any[]) => this._filterBonusesByStatus(list || [])),
      map((list: any[]) => this._groupBonusesByType(list || [])),
      tap(list => this._checkActiveBonuses(list)),
      catchError(error => of([])),
      tap(list => this.bonusList$.next(list)),
    ).subscribe();
  }

  public loadOriginalBonusList$() {
    return this._platformApi.bonusList(true).pipe(
      first(),
      map((res: any) => this._resolveBonusList(res?.data?.result?.info?.player?.walletsData?.walletList || [])),
      map((list: any[]) => this._mapBonuses(list || [])),
    );
  }

  /**
   * Merges bonus lists from multiple currencies into a single sorted list.
   * Maps currency data onto each bonus.
   * Sorts by createdAt date in reverse chronological order.
   */
  private _resolveBonusList(data: any[]) {
    const mergedBonusList = [];
    data.forEach(e => {
      const bonusByCurrencyList = e?.node?.bonusesData?.bonuseList
        ?.map(b => {
          return { ...b?.bonus, currency: e?.node?.currency, walletBonusBalanceId: e?.node?.id };
        })
        ?.sort((a, b) => new Date(a.createdAt)?.getTime() - new Date(b.createdAt)?.getTime())
        ?.reverse();
      mergedBonusList.push(...bonusByCurrencyList);
    });
    return mergedBonusList;
  }

  /**
   * Prepare bonus list for using in frontend
   *
   * @param list
   * @private
   */
  private _mapBonuses(list: Array<any>): Array<any> {
    list = list
      .map(bonus => {
        bonus = {
          ...bonus,
          currencySymbol: this._data.currencySymbol(bonus.currency),
          active: ![
            BonusStatus.CANCELED,
            BonusStatus.RELEASED,
            BonusStatus.ACTIVATION_EXPIRED,
            BonusStatus.USAGE_EXPIRED,
            BonusStatus.WAGERING_USAGE_EXPIRED,
          ].includes(bonus.status),
          activatable: [BonusStatus.NEW].includes(bonus.status),
          cancelable: [
            BonusStatus.NEW,
            BonusStatus.ACTIVE,
            BonusStatus.IN_PROGRESS,
            BonusStatus.FREESPIN_IN_PROGRESS,
            BonusStatus.FREESPIN_RELEASED,
          ].includes(bonus.status),
          expired: [BonusStatus.ACTIVATION_EXPIRED, BonusStatus.USAGE_EXPIRED, BonusStatus.WAGERING_USAGE_EXPIRED].includes(bonus.status),
          statusLabel: BonusStatusLabel[bonus.status] || bonus.status,
          createdAt: bonus.createdAt ? new Date(bonus.createdAt) : null,
          activationExpiresAfter: bonus.activationExpiresAfter ? new Date(bonus.activationExpiresAfter) : null,
          updatedAt: bonus.updatedAt ? new Date(bonus.updatedAt) : null,
          usageExpiresAfter: bonus.usageExpiresAfter ? new Date(bonus.usageExpiresAfter) : null,
          wageredAmountPercent: Number(this._calculateWageredAmountPercent(bonus)),
          isWelcome: this._isWelcomeBonus(bonus.name),
          name: this.mapBonusTitle(bonus.name),
        };
        return bonus;
      })
      .filter(bonus => !this._isBonusExpired(bonus));
    return list;
  }

  private _isWelcomeBonus(title: string) {
    return ['WLC', 'WCM', 'AFF'].some(key => title.toUpperCase().includes(key));
  }

  private _isBonusExpired(bonus: any) {
    const usageExpirationAfter = new Date(bonus.usageExpiresAfter);
    const currentDate = new Date();
    const extendedExpirationDate = new Date(usageExpirationAfter);
    extendedExpirationDate.setDate(extendedExpirationDate.getDate() + 7);
    return extendedExpirationDate < currentDate;
  }

  private _filterBonusesByStatus(list: any[]) {
    list = list.filter(bonus => this._bonusStatusListForShow.includes(bonus.status));
    return list;
  }

  /**
   * Groups the provided list of bonuses into separate lists for cash bonuses, freespin bonuses, and deposit bonuses.
   * Filters out any bonuses with a name that includes 'Welcome'.
   * Updates the corresponding RxJS subjects with the grouped bonus lists.
   * Sets a flag indicating whether the overall bonus list is empty.
   *
   * @param list - The list of bonuses to be grouped.
   * @returns The original list of bonuses, after being grouped.
   */
  private _groupBonusesByType(list: any[]) {
    const cashBonusList = [];
    const freespinBonusList = [];
    this.cashBonusList$.next(cashBonusList);
    this.freespinBonusList$.next(freespinBonusList);
    this.bonuses$.next(list);
    this._isBonusListEmpty = !list.filter(b => this._bonusStatusListForShow.includes(b.status)).length;
    return list;
  }

  /**
   * Maps the freespin bonus games in the provided list
   * by looking up the games in the games service
   * and adding the game object to each bonus.
   * @param list The list of bonuses to map games for
   */
  private _mapFreespinBonusGames(list: Array<any>) {
    const gameList = new Set();
    list.forEach(bonus => gameList.add(bonus?.gameId));
    return this._games.list({ 'external_id[]': [...gameList] }).pipe(
      map(res => {
        const gameListByExternalId = this._filters.valueAsKeyObject('externalId', res.gameList || []);
        list.forEach(b => {
          b.game = gameListByExternalId[b.gameId];
        });
        return list;
      }),
    );
  }

  public cmsBonusList$() {
    return this._offers
      .list({
        category_slug: 'welcome-bonuses-fiat',
      })
      .pipe(
        filter(bonuses => bonuses && bonuses.length),
        map(list => {
          const highrollerBonuses = this._separateBonuses(list, true);
          const starterBonuses = this._separateBonuses(list, false);
          return {
            [BONUS_PACKS.STARTER]: starterBonuses,
            [BONUS_PACKS.HIGHROLLER]: highrollerBonuses,
          };
        }),
        share(),
      );
  }

  /**
   * Separate bonuses data on 2 lists
   * @param list
   * @param isHighroller
   * @private
   */
  private _separateBonuses(list: any[], isHighroller: boolean): any[] {
    return list.map(item => {
      const filteredItem: any = {};
      for (const [key, value] of Object.entries(item)) {
        const hasStarter = key.toLowerCase().includes(BONUS_PACKS.STARTER);
        if (isHighroller ? !hasStarter : hasStarter || key === 'Title' || key === 'Image' || key === 'id' || key === 'BOIdentifier') {
          filteredItem[key] = value;
        }
      }
      return filteredItem;
    });
  }

  /**
   * Get merged bonuses from CMS and SS
   */
  public welcomBonuses$() {
    return this.cmsBonusList$().pipe(
      map(bonusListCms => {
        return {
          [BONUS_PACKS.STARTER]: this._markActiveBonus(bonusListCms[BONUS_PACKS.STARTER]),
          [BONUS_PACKS.HIGHROLLER]: this._markActiveBonus(bonusListCms[BONUS_PACKS.HIGHROLLER]),
        };
      }),
      map(list => {
        return {
          [BONUS_PACKS.STARTER]: this._markActiveBonusIdx(list, BONUS_PACKS.STARTER),
          [BONUS_PACKS.HIGHROLLER]: this._markActiveBonusIdx(list, BONUS_PACKS.HIGHROLLER),
        };
      }),
    );
  }

  /**
   * Mark active bonus idx
   * @param list
   * @param bonusPackType
   * @private
   */
  private _markActiveBonusIdx(list: object, bonusPackType: string): any[] {
    return list[bonusPackType].map(item => {
      return {
        ...item,
        activeBonusIdx: list[bonusPackType].findIndex(e => e.active),
      };
    });
  }

  /**
   * Mark active bonus and resolve active bonus image
   * @param bonusListCMS
   * @param bonusListSS
   * @param bonusPack
   * @private
   */
  private _markActiveBonus(bonusListCMS: any[]) {
    if (this._user.auth) {
      if (this._userGroup.isExistGroup(bonusListCMS.map(e => e.BOIdentifier)[bonusListCMS.length - 1])) {
        this.isAllBonusUsed = true;
        bonusListCMS.forEach(e => {
          e.used = true;
        });
      } else {
        const lastTakenBonusIndex = bonusListCMS?.findIndex(
          item => this._user?.info?.segments?.includes(item?.BOIdentifier) && item?.BOIdentifier,
        );
        let activeBonus = bonusListCMS[lastTakenBonusIndex + 1];
        if (!activeBonus) {
          activeBonus = bonusListCMS[0];
        }
        const activeBonusIndex = bonusListCMS.findIndex(e => e.id === activeBonus.id);
        bonusListCMS.forEach((e, i) => {
          if (i < activeBonusIndex) {
            e.used = true;
          }
        });
        activeBonus.active = true;
      }
      return bonusListCMS;
    } else {
      bonusListCMS[0].active = true;
      return bonusListCMS;
    }
  }

  /**
   * Update bonus status
   * @param bonus
   */
  updateBonusStatus(bonus): Observable<any> {
    return this._platformApi.updateBonusStatus(bonus);
  }

  /**
   * Bonus deposit code activation
   *
   * @param code
   */
  public activateDepositBonusCode(code): Observable<any> {
    // return this._api.playerSetBonusCode({deposit_bonus_code: (code || '').toUpperCase()});
    return of(true);
  }

  /**
   * Bonus deposit code activation
   *
   * @param code
   */
  public activateCouponBonusCode(code): Observable<any> {
    // return this._api.playerSetCoupon({coupon_code: (code || '').toUpperCase()});
    return of(true);
  }

  /**
   * Bonus deposit code clear
   *
   */
  public deleteDepositBonusCode(): Observable<any> {
    // return this._api.playerClearBonusCode();
    return of(true);
  }

  /**
   * Prepare bonus title for using in frontend
   *
   * @private
   */
  public mapBonusTitle(title: string): string {
    return (title || '')
      .replace(/\[(.*?)]/g, '')
      .replace(/\[(.*?)}/g, '')
      .replace(/\{(.*?)]/g, '')
      .replace(/\{(.*?)}/g, '');
  }

  /**
   * Send request on update bonus settings
   */
  changeIssues(state: boolean) {
    // return this._api.playerUpdateBonusSettings({can_issue: state});
    return of(true);
  }

  /**
   * Clear bonus title ([WCM*] First Deposit Bonus -> First Deposit Bonus )
   * @param bonus
   */
  private _clearBonusTitle(bonus: { name: string }) {
    if (bonus.name) {
      bonus.name = bonus.name.replace(/\[([^)]+)\]/gi, '').trim();
    }
  }

  /**
   * Compare bonus list and return active bonus
   * @param bonusListCMS
   * @param bonusListSS
   * @param bonusPack
   * @returns
   */
  public findActiveBonus(bonusListCMS: any[], bonusListSS: any[], bonusPack?: string) {
    return bonusListCMS.find(cmsBonus => {
      return bonusListSS.find(ssBonus => {
        if (
          ssBonus?.bonus?.id?.includes(bonusPack !== BONUS_PACKS.STARTER ? cmsBonus.BOIdentifier : cmsBonus.BOIdentifierStarter) ||
          ssBonus?.freespins?.id?.includes(bonusPack !== BONUS_PACKS.STARTER ? cmsBonus.BOIdentifier : cmsBonus.BOIdentifierStarter)
        ) {
          return ssBonus;
        } else {
          return null;
        }
      });
    });
  }

  /**
   * Calculates the percentage of the wagered amount for a given bonus.
   * @param bonus - The bonus object containing the wager amount and wagered amount.
   * @returns The percentage of the wagered amount as a string, or `null` if the wager amount or wagered amount is missing.
   */
  private _calculateWageredAmountPercent(bonus) {
    if (bonus?.wagerAmount && bonus?.wageredAmount) {
      return Number((bonus?.wageredAmount / bonus?.wagerAmount) * 100).toFixed(2) || 0;
    }
    return null;
  }

  /**
   * Checks the list of bonuses and sets flags indicating whether there are any active free spin or cash bonuses.
   * @param list - The list of bonuses to check.
   */
  private _checkActiveBonuses(list: any[] = []) {
    this._hasActiveFsBonus = list?.some(e => e?.type === BonusType.FREESPIN && e?.status === BonusStatus.ACTIVE);
    this._hasActiveCashBonus = list?.some(e => e?.type === BonusType.CASH && e?.status === BonusStatus.ACTIVE);
  }

}
